/**
 * Spec 088c — ProtectedRoute wrapper.
 *
 * Renders its children only when the current user satisfies the `requireRoles`
 * any-of check. If the user has none of the listed roles, a 403 panel is
 * rendered (NOT a blank page, NOT a runtime crash). While the initial auth
 * fetch is in flight, a small loading placeholder is shown to avoid layout
 * shift.
 *
 * Usage (in App.tsx):
 *   <Route
 *     path="admin"
 *     element={
 *       <ProtectedRoute requireRoles={['rule_admin', 'orbit:admin']}>
 *         <AdminHomePage />
 *       </ProtectedRoute>
 *     }
 *   />
 *
 * Semantics:
 *   - `requireRoles` is any-of (if user has at least one listed role, pass).
 *   - Omitting `requireRoles` makes this a passthrough — children always
 *     render. Useful for future non-role-based guards (e.g. tenant-scoped).
 *   - `hasRole` auto-prefixes `orbit:` for non-namespaced inputs.
 */
import type { ReactNode } from 'react';
import { Icon } from './ui/Icon.js';
import { useAuth } from '../lib/auth-context.js';

export interface ProtectedRouteProps {
  /** Any-of role check. Omit to disable role gating (passthrough). */
  requireRoles?: string[];
  children: ReactNode;
}

export function ProtectedRoute({
  requireRoles,
  children,
}: ProtectedRouteProps) {
  const { hasRole, isLoading, user } = useAuth();

  // Loading state — render a small placeholder to prevent layout shift.
  if (isLoading) {
    return (
      <div
        data-testid="protected-route-loading"
        className="flex min-h-[40vh] items-center justify-center"
      >
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Icon name="bx-lock" className="animate-pulse" />
          <span>Checking access…</span>
        </div>
      </div>
    );
  }

  // No gate configured → passthrough.
  if (!requireRoles || requireRoles.length === 0) {
    return <>{children}</>;
  }

  // User not loaded (post-loading) OR has none of the required roles → 403.
  const allowed = user !== null && requireRoles.some((r) => hasRole(r));
  if (!allowed) {
    return (
      <div
        data-testid="protected-route-403"
        className="mx-auto mt-10 max-w-lg rounded-lg border border-rose-200 bg-rose-50 p-6 text-center"
      >
        <div className="flex justify-center">
          <div className="rounded-full bg-rose-100 p-3">
            <Icon name="bx-shield-x" className="text-rose-700" />
          </div>
        </div>
        <h2 className="mt-3 text-lg font-semibold text-rose-900">
          Access denied
        </h2>
        <p className="mt-2 text-sm text-rose-800">
          This area is restricted to users with one of the following roles:{' '}
          <strong className="font-mono">{requireRoles.join(', ')}</strong>.
        </p>
        <p className="mt-3 text-xs text-rose-700">
          If you believe you should have access, contact an administrator.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
